/**
 * Work Plan Export/Import Utilities
 * Handles exporting and importing work plans, tasks, and templates
 */

const EXPORT_VERSION = '1.0';

/**
 * Export work plans, tasks, and templates to JSON
 * @param {Object} options - Export options
 * @param {Array} options.plans - Array of work plans to export
 * @param {Array} options.tasks - Array of tasks to export
 * @param {Array} options.templates - Array of templates to export (optional)
 * @param {Array} options.selectedPlanIds - Array of plan IDs to export (if empty, exports all)
 * @returns {Object} Export data object
 */
export const exportWorkPlans = ({ plans, tasks, templates = [], selectedPlanIds = [] }) => {
  // Filter plans if specific ones are selected
  const plansToExport = selectedPlanIds.length > 0
    ? plans.filter(plan => selectedPlanIds.includes(plan.id))
    : plans;

  // Get plan IDs to export
  const exportedPlanIds = plansToExport.map(plan => plan.id);

  // Filter tasks that belong to exported plans
  const tasksToExport = tasks.filter(task => exportedPlanIds.includes(task.planId));

  // Filter templates (optional, can include all or none)
  const templatesToExport = templates || [];

  const exportData = {
    version: EXPORT_VERSION,
    exportDate: new Date().toISOString(),
    plans: plansToExport,
    tasks: tasksToExport,
    templates: templatesToExport,
  };

  return exportData;
};

/**
 * Download export data as JSON file
 * @param {Object} exportData - Export data object
 * @param {string} filename - Optional filename (default: workplans-export-YYYY-MM-DD.json)
 */
export const downloadExportFile = (exportData, filename = null) => {
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  
  // Generate filename if not provided
  if (!filename) {
    const date = new Date().toISOString().split('T')[0];
    filename = `workplans-export-${date}.json`;
  }

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Validate import file structure
 * @param {Object} data - Import data to validate
 * @returns {Object} Validation result { valid: boolean, error?: string }
 */
export const validateImportData = (data) => {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid file format. Expected JSON object.' };
  }

  if (!data.version) {
    return { valid: false, error: 'Missing version field. This may be an invalid export file.' };
  }

  if (!Array.isArray(data.plans)) {
    return { valid: false, error: 'Invalid file format. Plans must be an array.' };
  }

  if (!Array.isArray(data.tasks)) {
    return { valid: false, error: 'Invalid file format. Tasks must be an array.' };
  }

  // Validate plan structure
  for (const plan of data.plans) {
    if (!plan.id || !plan.name) {
      return { valid: false, error: 'Invalid plan structure. Plans must have id and name fields.' };
    }
  }

  // Validate task structure
  for (const task of data.tasks) {
    if (!task.id || !task.planId || !task.title) {
      return { valid: false, error: 'Invalid task structure. Tasks must have id, planId, and title fields.' };
    }
  }

  return { valid: true };
};

/**
 * Read and parse import file
 * @param {File} file - File object to read
 * @returns {Promise<Object>} Parsed JSON data
 */
export const readImportFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      reject(new Error('Invalid file type. Please select a JSON file.'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        resolve(jsonData);
      } catch (error) {
        reject(new Error('Failed to parse JSON file: ' + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Import work plans data
 * @param {Object} importData - Import data object
 * @param {string} mergeStrategy - 'merge' or 'replace'
 * @param {Function} generateNewId - Function to generate new IDs for imported items
 * @returns {Object} Imported data with new IDs
 */
export const importWorkPlans = (importData, mergeStrategy = 'merge', generateNewId = null) => {
  const validation = validateImportData(importData);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Default ID generator
  const idGenerator = generateNewId || ((prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Create ID mapping for imported items
  const planIdMap = new Map();
  const taskIdMap = new Map();

  // Process plans - generate new IDs
  const importedPlans = importData.plans.map(plan => {
    const newId = idGenerator('plan');
    planIdMap.set(plan.id, newId);
    return {
      ...plan,
      id: newId,
      createdAt: plan.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });

  // Process tasks - generate new IDs and update planId references
  const importedTasks = importData.tasks.map(task => {
    const newId = idGenerator('task');
    const newPlanId = planIdMap.get(task.planId);
    
    if (!newPlanId) {
      // Task references a plan that wasn't imported, skip it
      return null;
    }

    taskIdMap.set(task.id, newId);
    return {
      ...task,
      id: newId,
      planId: newPlanId,
      createdAt: task.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }).filter(task => task !== null);

  // Process templates - generate new IDs
  const importedTemplates = (importData.templates || []).map(template => {
    const newId = idGenerator('template');
    return {
      ...template,
      id: newId,
      createdAt: template.createdAt || new Date().toISOString(),
    };
  });

  return {
    plans: importedPlans,
    tasks: importedTasks,
    templates: importedTemplates,
  };
};

